import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MagnifyingGlass } from "@phosphor-icons/react";
import api from "@/lib/api";
import CreatorCard from "@/components/CreatorCard";
import PostCard from "@/components/PostCard";
import { startCheckout } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [creators, setCreators] = useState([]);
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const category = params.get("category") || "all";
  const tab = params.get("tab") || (params.get("post_type") ? "content" : "creators");
  const postType = params.get("post_type") || "";

  useEffect(() => { api.get("/categories").then(({ data }) => setCategories(data)); }, []);

  const load = useCallback(() => {
    const cat = category !== "all" ? `&category=${category}` : "";
    const qq = q ? `&q=${encodeURIComponent(q)}` : "";
    api.get(`/creators?x=1${cat}${qq}`).then(({ data }) => setCreators(data));
    const pt = postType ? `&post_type=${postType}` : "";
    api.get(`/posts/explore?x=1${cat}${qq}${pt}`).then(({ data }) => setPosts(data));
  }, [category, q, postType]);

  useEffect(() => { load(); }, [load]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(params);
    if (slug === "all") p.delete("category"); else p.set("category", slug);
    setParams(p);
  };
  const setTab = (t) => {
    const p = new URLSearchParams(params);
    p.set("tab", t);
    if (t !== "content") p.delete("post_type");
    setParams(p);
  };

  const onCheckout = (post) => {
    if (!user) return navigate("/login");
    if (post.visibility === "ppv") startCheckout({ kind: "ppv", resource_id: post.id });
    else startCheckout({ kind: "subscription", resource_id: post.creator.id });
  };

  return (
    <div className="max-w-7xl mx-auto px-5 py-10 md:py-14" data-testid="explore-page">
      <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="font-display text-5xl md:text-7xl leading-none mb-8">
        Explore the <span className="italic text-primary">fantasy</span>
      </motion.h1>

      {/* search */}
      <div className="relative mb-6 max-w-xl">
        <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid="explore-search"
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search creators, tags, stories…"
          className="w-full bg-card border border-border pl-12 pr-4 py-3.5 focus:border-primary outline-none transition-colors"
        />
      </div>

      {/* category chips */}
      <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2 mb-6">
        <button data-testid="cat-chip-all" onClick={() => setCategory("all")}
          className={`px-4 py-2 shrink-0 font-accent text-xs uppercase tracking-widest border transition-colors ${category === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>All</button>
        {categories.map((c) => (
          <button key={c.slug} data-testid={`cat-chip-${c.slug}`} onClick={() => setCategory(c.slug)}
            className={`px-4 py-2 shrink-0 font-accent text-xs uppercase tracking-widest border transition-colors ${category === c.slug ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{c.label}</button>
        ))}
      </div>

      {/* tabs */}
      <div className="flex gap-6 border-b border-border mb-8">
        {["creators", "content"].map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`pb-3 font-accent text-sm uppercase tracking-widest transition-colors border-b-2 -mb-px ${tab === t ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{t}</button>
        ))}
      </div>

      {tab === "creators" ? (
        creators.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {creators.map((c, i) => <CreatorCard key={c.id} creator={c} index={i} />)}
          </div>
        ) : <Empty text="No creators found. Be the first to join." />
      ) : (
        posts.length ? (
          <div className="max-w-2xl mx-auto flex flex-col gap-6">
            {posts.map((p, i) => <PostCard key={p.id} post={p} index={i} onCheckout={onCheckout} />)}
          </div>
        ) : <Empty text="No content yet in this category." />
      )}
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="py-24 text-center text-muted-foreground" data-testid="empty-state">
    <p className="font-display text-3xl mb-2">Nothing here… yet</p>
    <p>{text}</p>
  </div>
);
